import { Injectable } from '@angular/core';
import { Logger } from '@core/logger';
import {
	getBusinessTemplateStorageRefByDocument,
	getMatterDocumentStorageRef,
} from '@core/util/documents';
import { createId } from '@core/util/file';
import { CollectionPaths } from '@models/collections';
import { Context, MatterSource } from '@models/matter';
import {
	DocumentCategory,
	MatterWordDocument,
} from '@models/matter-word-documents';
import { GlobalWordDocumentTemplate } from '@models/documents';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Action, State, StateContext } from '@ngxs/store';
import {
	CopyTemplateParams,
	DeleteContentControlsRequest,
	DocumentType,
	WordDocumentMetadata,
	WordDocumentStyles,
} from '@services/docx-document/docx-document.models';
import { DocxDocumentService } from '@services/docx-document/docx-document.service';
import { UploadService } from '@services/file/upload.service';
import { AuthState } from '@state/auth';
import { BusinessState } from '@state/business';
import { BusinessBaseDocxsState } from '@state/business/word/business-base-docxs/business-base-docxs.state';
import { BusinessWordDocumentsState } from '@state/business/word/business-word-documents/business-word-documents.state';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { FirestoreStateBaseForMatterWithSubscription } from '@state/FirestoreStateBaseForMatter';
import { take } from 'rxjs/operators';
import { ToastService } from '../../../../ui/toast/toast.service';
import { MatterPackagesDispatch } from '../matter-packages/matter-packages.dispatch';
import { MatterPackagesState } from '../matter-packages/matter-packages.state';
import { SelectedMatter } from '../matter.selectors';
import * as actions from './matter-word-documents.actions';
import { environment } from 'src/environments/environment';
import {
	MATTER_WORD_DOCUMENTS_STATE_TOKEN,
	MatterWordDocumentsModel,
} from './matter-word-documents.state.model';
import {
	MatterPackage,
	MatterPackageContentType,
	MatterPackageContentDocumentType,
} from '@models/matter-package';
import { firstValueFrom } from 'rxjs';

type Ctx = StateContext<MatterWordDocumentsModel>;

const DEFAULT_STATE: MatterWordDocumentsModel = {
	...DEFAULT_STATE_BASE,
	items: [],
	status: {},
	itemsLoading: [],
	itemsReseting: [],
	itemsDeleting: [],
	itemsDuplicating: [],
	itemsOpening: [],
	itemsExporting: [],
};

@State<MatterWordDocumentsModel>({
	name: MATTER_WORD_DOCUMENTS_STATE_TOKEN,
	defaults: DEFAULT_STATE,
})
@Injectable()
export class MatterWordDocumentsState extends FirestoreStateBaseForMatterWithSubscription<
	MatterWordDocument,
	MatterWordDocumentsModel
> {
	constructor(
		private docxService: DocxDocumentService,
		private uploadService: UploadService,
		private toastService: ToastService,
		private matterPackagesDispatch: MatterPackagesDispatch,
		private docxDocumentService: DocxDocumentService,
	) {
		super(
			(pathParams) => {
				return CollectionPaths.business_matter_matterWordDocuments(
					pathParams.businessId,
					pathParams.matterId,
				);
			},
			(doc) => {
				return { items: doc as MatterWordDocument[] };
			},
			() => DEFAULT_STATE,
		);
	}

	@SelectSnapshot(BusinessWordDocumentsState.getAllMerged)
	businessWordDocuments: GlobalWordDocumentTemplate[];

	@SelectSnapshot(BusinessBaseDocxsState.getBaseDocxPathForOffice)
	getBaseDocxPathForOfficeFn: (officeId: string, referenceId: string) => string;

	@SelectSnapshot(SelectedMatter.officeId) officeId: string;

	@SelectSnapshot(AuthState.getUserId) userId: string;

	@SelectSnapshot(BusinessState.getBusinessWordDocumentStyles)
	getBusinessWordDocumentStyles: WordDocumentStyles;

	@SelectSnapshot(SelectedMatter.matterSource) matterSource: MatterSource;
	@SelectSnapshot(SelectedMatter.getLeapMatterId) leapMatterId: string;

	@SelectSnapshot(MatterPackagesState.getAllPackages)
	allPackages: MatterPackage[];

	@Action(actions.Create)
	async create(
		ctx: Ctx,
		{ document, autoOpen = false, duplicated = false }: actions.Create,
	) {
		let documentToCreate: Partial<MatterWordDocument>;
		try {
			// If no payload is provided, create a blank document
			const newId = createId(7);
			documentToCreate = document || {
				id: newId,
				title: 'Blank Document',
				source: null,
				parentVersion: 1,
				version: 0,
				useGlobal: false,
				isBasedOnCloserTemplate: false,
				versions: [
					{
						createdAt: Date.now(),
						parentVersion: 1,
						version: 0,
					},
				],
				destinationStorageRef: getMatterDocumentStorageRef(
					this.businessId,
					this.matterId,
					newId,
				),
				context: {},
				contextTypes: [],
				sourceStorageRef: null,
				updatedAt: Date.now(),
			};

			ctx.patchState({
				itemsLoading: [...ctx.getState().itemsLoading, documentToCreate.id],
			});
			const params: CopyTemplateParams = await this.getCopyTemplateParams(
				documentToCreate as MatterWordDocument,
			);
			if (documentToCreate.sourceStorageRef) {
				await this.docxService.copyTemplate(params).pipe(take(1)).toPromise();
			}

			if (!duplicated) {
				this.toastService.success('Document added successfully');
			}

			await this.repo.CreateOrMerge(documentToCreate.id, {
				...documentToCreate,
				businessId: this.businessId,
				createdBy: this.userId,
				createdAt: Date.now(),
			});

			// This is here to force the "save" event to be triggered and we can push the document to leap.
			//This is because we don't have an onCreate event just yet.
			// Refactor this to use a "onCreate" event when available.
			if (this.leapMatterId) {
				await this.docxDocumentService
					.updateMetadataWitLocking(
						document.destinationStorageRef,
						environment.webdav.baseUrl,
						params.parameters,
						document.context ? JSON.stringify(document.context, null, 0) : null,
						this.businessId,
						this.matterId,
						document.id,
					)
					.pipe(take(1))
					.toPromise();
			}

			if (autoOpen) {
				await ctx.dispatch(
					new actions.OpenDocumentInWord(
						documentToCreate as MatterWordDocument,
					),
				);
			}
		} catch (error) {
			this.toastService.error('Error adding document');
			Logger.exception(error);
			throw error;
		} finally {
			ctx.patchState({
				itemsLoading: ctx
					.getState()
					.itemsLoading.filter(
						(id) => id !== (document?.id || documentToCreate.id),
					),
			});
		}
	}

	@Action(actions.CreateBatch)
	async createBatch(
		ctx: Ctx,
		{ documents, autoOpen = false, duplicated = false }: actions.CreateBatch,
	) {
		if (!documents?.length) return;

		const documentsToCreate: Partial<MatterWordDocument>[] = [];
		const loadingIds: string[] = [];

		try {
			// Prepare all documents for creation
			for (const document of documents) {
				const newId = document?.id || createId(7);
				const documentToCreate: Partial<MatterWordDocument> = document || {
					id: newId,
					title: 'Blank Document',
					source: null,
					parentVersion: 1,
					version: 0,
					useGlobal: false,
					isBasedOnCloserTemplate: false,
					versions: [
						{
							createdAt: Date.now(),
							parentVersion: 1,
							version: 0,
						},
					],
					destinationStorageRef: getMatterDocumentStorageRef(
						this.businessId,
						this.matterId,
						newId,
					),
					context: {},
					contextTypes: [],
					sourceStorageRef: null,
					updatedAt: Date.now(),
				};
				documentsToCreate.push(documentToCreate);
				loadingIds.push(documentToCreate.id);
			}

			// Set all documents as loading
			ctx.patchState({
				itemsLoading: [...ctx.getState().itemsLoading, ...loadingIds],
			});

			// Process all copy template operations
			const copyPromises = documentsToCreate.map(async (documentToCreate) => {
				const params: CopyTemplateParams = await this.getCopyTemplateParams(
					documentToCreate as MatterWordDocument,
				);
				if (documentToCreate.sourceStorageRef) {
					await this.docxService.copyTemplate(params).pipe(take(1)).toPromise();
				}
				return { documentToCreate, params };
			});

			const copyResults = await Promise.all(copyPromises);

			// Create all documents in batch
			const createPromises = copyResults.map(({ documentToCreate }) =>
				this.repo.CreateOrMerge(documentToCreate.id, {
					...documentToCreate,
					businessId: this.businessId,
					createdBy: this.userId,
					createdAt: Date.now(),
				}),
			);
			await Promise.all(createPromises);

			// Handle leap metadata updates if needed
			if (this.leapMatterId) {
				const leapPromises = copyResults.map(({ documentToCreate, params }) =>
					this.docxDocumentService
						.updateMetadataWitLocking(
							documentToCreate.destinationStorageRef,
							environment.webdav.baseUrl,
							params.parameters,
							documentToCreate.context
								? JSON.stringify(documentToCreate.context, null, 0)
								: null,
							this.businessId,
							this.matterId,
							documentToCreate.id,
						)
						.pipe(take(1))
						.toPromise(),
				);
				await Promise.all(leapPromises);
			}

			if (!duplicated) {
				this.toastService.success(
					`${documents.length} documents added successfully`,
				);
			}

			// Handle auto-open if requested (only open first document to avoid overwhelming user)
			if (autoOpen && documentsToCreate.length > 0) {
				await ctx.dispatch(
					new actions.OpenDocumentInWord(
						documentsToCreate[0] as MatterWordDocument,
					),
				);
			}
		} catch (error) {
			this.toastService.error('Error adding documents');
			Logger.exception(error);
		} finally {
			// Remove all documents from loading state
			ctx.patchState({
				itemsLoading: ctx
					.getState()
					.itemsLoading.filter((id) => !loadingIds.includes(id)),
			});
		}
	}

	@Action(actions.ResetDocument)
	async resetDocument(ctx: Ctx, { payload }: actions.ResetDocument) {
		ctx.patchState({
			itemsReseting: [...ctx.getState().itemsReseting, payload.id],
		});
		try {
			const template = this.businessWordDocuments.find(
				(doc) => doc.source === payload.templateId,
			);

			if (!template) {
				throw new Error(`Template not found for source: ${payload.source}`);
			}

			const deleteRequest: DeleteContentControlsRequest = {
				businessId: this.businessId,
				matterId: this.matterId,
				documentId: payload.id,
			};
			await this.errorHandler(
				firstValueFrom(
					this.docxService.deleteContentControlsForDocument(deleteRequest),
				),
			);
			const storageRef = getBusinessTemplateStorageRefByDocument(template);
			const params: CopyTemplateParams = await this.getCopyTemplateParams({
				...payload,
				sourceStorageRef: storageRef,
			});

			await this.docxService.copyTemplate(params).pipe(take(1)).toPromise();

			await this.repo.CreateOrMerge(payload.id, {
				...payload,
				updatedAt: Date.now(),
			});

			this.toastService.success('Document reset successfully');
		} catch (error) {
			this.toastService.error('Error resetting document');
			Logger.exception(error);
		} finally {
			ctx.patchState({
				itemsReseting: ctx
					.getState()
					.itemsReseting.filter((id) => id !== payload.id),
			});
		}
	}

	@Action(actions.UpdateDocument)
	updateDocument(ctx: Ctx, { document }: actions.UpdateDocument) {
		this.errorHandler(this.repo.CreateOrMerge(document.id, document));
	}

	@Action(actions.UpdateTitle)
	updateTitle(ctx: Ctx, { docId, title }: actions.UpdateTitle) {
		const document = ctx.getState().items.find((doc) => doc.id === docId);
		if (!document) {
			return;
		}
		this.errorHandler(
			this.repo.CreateOrMerge(docId, {
				...document,
				title,
			}),
		);
	}

	@Action(actions.Remove)
	async removeDocument(ctx: Ctx, { document }: actions.Remove): Promise<void> {
		ctx.patchState({
			itemsDeleting: [...ctx.getState().itemsDeleting, document.id],
		});
		try {
			const packagesWithDocument = this.allPackages?.filter((pkg) =>
				pkg.contents?.some((content) => content.id === document.id),
			);

			for (const pkg of packagesWithDocument || []) {
				await this.matterPackagesDispatch.RemoveContent({
					packageId: pkg.id,
					contentId: document.id,
				});
			}

			const deleteRequest: DeleteContentControlsRequest = {
				businessId: this.businessId,
				matterId: this.matterId,
				documentId: document.id,
			};
			await this.errorHandler(
				firstValueFrom(
					this.docxService.deleteContentControlsForDocument(deleteRequest),
				),
			);

			await this.errorHandler(this.repo.Delete(document.id));
			await this.errorHandler(
				this.uploadService.delete(document.destinationStorageRef),
			);

			this.toastService.success('Document deleted successfully');
		} catch (error) {
			this.toastService.error('Error deleting document');
			Logger.exception(error);
		} finally {
			ctx.patchState({
				itemsDeleting: ctx
					.getState()
					.itemsDeleting.filter((id) => id !== document.id),
			});
		}
	}

	@Action(actions.ExportDocument)
	async exportDocument(ctx: Ctx, { document }: actions.ExportDocument) {
		ctx.patchState({
			itemsExporting: [...ctx.getState().itemsExporting, document.id],
		});

		try {
			await this.docxService.exportDocument(document.id);
		} catch (error) {
			this.toastService.error('Error exporting document');
			Logger.exception(error);
		} finally {
			ctx.patchState({
				itemsExporting: ctx
					.getState()
					.itemsExporting.filter((id) => id !== document.id),
			});
		}
	}

	@Action(actions.DuplicateDocument)
	async duplicateDocument(
		ctx: Ctx,
		{ document, includeInPackageId }: actions.DuplicateDocument,
	) {
		ctx.patchState({
			itemsDuplicating: [...ctx.getState().itemsDuplicating, document.id],
		});
		try {
			const newId = createId(7);
			const newDocument: MatterWordDocument = {
				...document,
				id: newId,
				source: newId,
				parentVersion: 1,
				version: 0,
				useGlobal: false,
				isBasedOnCloserTemplate: false,
				duplicatedFromId: document.id,
				title: `${document.title} - COPY`,
				versions: [
					{
						createdAt: Date.now(),
						parentVersion: 1,
						version: 0,
					},
				],
				updatedAt: Date.now(),
				lastEditedBy: this.userId,
				createdBy: this.userId,
				createdAt: Date.now(),
			};

			newDocument.destinationStorageRef = getMatterDocumentStorageRef(
				this.businessId,
				this.matterId,
				newId,
			);

			const isUploadFile = newDocument?.categories?.some(
				(x) =>
					x === DocumentCategory.UPLOAD ||
					x === DocumentCategory.SYNC_FORMS ||
					x === DocumentCategory.MATTER_FORMS,
			);

			if (!isUploadFile) {
				await this.errorHandler(this.repo.CreateOrMerge(newId, newDocument));
				const params = await this.getCopyTemplateParams(newDocument);
				await firstValueFrom(this.docxService.copyTemplate(params));
			}

			this.toastService.success('Document duplicated successfully');

			if (includeInPackageId) {
				let docType = MatterPackageContentType.MATTER_DOC;
				if (newDocument?.categories?.includes(DocumentCategory.UPLOAD))
					docType = MatterPackageContentType.UPLOADED_FILE;
				if (newDocument?.categories?.includes(DocumentCategory.SYNC_FORMS))
					docType = MatterPackageContentType.SYNC_FORM;
				if (newDocument?.categories?.includes(DocumentCategory.MATTER_FORMS))
					docType = MatterPackageContentType.MATTER_FORM;

				this.matterPackagesDispatch.AddContent({
					docMetadata: {
						id: includeInPackageId,
						title: newDocument.title,
						type: docType,
						contentType: newDocument?.sourceStorageRef?.endsWith('.docx')
							? MatterPackageContentDocumentType.WORD
							: MatterPackageContentDocumentType.PDF,
						content: newDocument,
					},
					duplicated: true,
				});
			}
		} catch (error) {
			this.toastService.error('Error duplicating document');
			Logger.exception(error);
		} finally {
			ctx.patchState({
				itemsDuplicating: ctx
					.getState()
					.itemsDuplicating.filter((id) => id !== document.id),
			});
		}
	}

	@Action(actions.OpenDocumentInWord)
	async openDocumentInWord(ctx: Ctx, { document }: actions.OpenDocumentInWord) {
		ctx.patchState({
			itemsOpening: [...ctx.getState().itemsOpening, document.id],
		});
		try {
			await this.docxService.openDocumentInWord(document.id);
		} catch (error) {
			this.toastService.error('Error opening document');
			Logger.exception(error);
		} finally {
			ctx.patchState({
				itemsOpening: ctx
					.getState()
					.itemsOpening.filter((id) => id !== document.id),
			});
		}
	}

	private async getCopyTemplateParams(
		matterDocument: MatterWordDocument,
	): Promise<CopyTemplateParams> {
		const params: CopyTemplateParams = {
			title: matterDocument.title,
			parameters:
				this.matterSource === MatterSource.LEAP
					? await this.getMatterCopyTemplateDocumentMetadataForLeap(
							matterDocument.context,
							this.matterId,
							this.leapMatterId,
							this.matterSource,
							matterDocument.id,
							matterDocument.leapDocumentId,
						)
					: this.getMatterCopyTemplateDocumentMetadata(
							matterDocument.context,
							this.matterId,
							this.matterSource,
							matterDocument.id,
						),
			wordDocumentStyles: matterDocument.ignoreBusinessStylingSettings
				? null
				: this.getBusinessWordDocumentStyles,
			sourcePath: matterDocument.sourceStorageRef,
			destinationPath: matterDocument.destinationStorageRef,
			baseDocumentPath: matterDocument.baseDocxReferenceId
				? this.getBaseDocxPathForOfficeFn(
						this.officeId,
						matterDocument.baseDocxReferenceId,
					)
				: null,
		};

		return params;
	}

	private getMatterCopyTemplateDocumentMetadata(
		context: Context,
		matterId: string,
		matterSource: MatterSource,
		closerDocumentId: string,
	): WordDocumentMetadata {
		const metadata: WordDocumentMetadata = {
			Context: context ? JSON.stringify(context, null, 0) : null,
			DocumentType: DocumentType.MATTER_DOCUMENT,
			CloserMatterId: matterId,
			PushToLeap: matterSource === MatterSource.LEAP ? true : false,
			CloserDocumentId: closerDocumentId,
		};

		return metadata;
	}

	private async getMatterCopyTemplateDocumentMetadataForLeap(
		context: Context,
		closerMatterId: string,
		leapMatterId: string,
		matterSource: MatterSource,
		closerDocumentId: string,
		leapDocumentId: string = null,
	): Promise<WordDocumentMetadata> {
		const metadata: WordDocumentMetadata = {
			...this.getMatterCopyTemplateDocumentMetadata(
				context,
				closerMatterId,
				matterSource,
				closerDocumentId,
			),
			// LeapFirmId: leapFirmId,
			// LeapUserId: leapUserId,
			// LeapAccessToken: accessToken,
			MatterId: leapMatterId,
		};

		if (leapDocumentId) {
			metadata.LeapDocumentId = leapDocumentId;
		}

		return metadata;
	}

	private async errorHandler<T>(promise: Promise<T>): Promise<T> {
		try {
			return await promise;
		} catch (error) {
			Logger.exception(error);
		}
	}
}
